import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adjacentAdminUserFormSection,
  filterAdminUserFormSections,
  scrollToAdminUserFormSection,
} from '../domain/adminUser/adminUserFormSections';

/**
 * Section scroll-spy + top-to-bottom navigation for Add/Edit user forms.
 *
 * @param {object} opts
 * @param {boolean} opts.isStudent
 * @param {'add'|'edit'} opts.mode
 * @param {boolean} [opts.enabled]
 * @param {boolean} opts.additionalExpanded
 * @param {(open: boolean) => void} opts.onExpandAdditional
 */
export default function useAdminUserFormSectionNav({
  isStudent,
  mode,
  enabled = true,
  additionalExpanded,
  onExpandAdditional,
}) {
  const sections = useMemo(
    () => filterAdminUserFormSections({ isStudent, mode }),
    [isStudent, mode]
  );

  const [activeId, setActiveId] = useState(sections[0]?.id || null);

  useEffect(() => {
    if (!sections.length) {
      setActiveId(null);
      return;
    }
    setActiveId((prev) =>
      sections.some((s) => s.id === prev) ? prev : sections[0].id
    );
  }, [sections]);

  useEffect(() => {
    if (!enabled || !sections.length) return undefined;

    const nodes = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);

    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0.1, 0.25, 0.5],
      }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [enabled, sections, additionalExpanded]);

  const goTo = useCallback(
    (sectionId) => {
      const target = sections.find((s) => s.id === sectionId);
      if (!target) return;

      const runScroll = () => {
        scrollToAdminUserFormSection(sectionId);
        setActiveId(sectionId);
      };

      if (target.requiresExpand && !additionalExpanded) {
        onExpandAdditional?.(true);
        window.setTimeout(runScroll, 80);
        return;
      }
      runScroll();
    },
    [sections, additionalExpanded, onExpandAdditional]
  );

  const goNext = useCallback(() => {
    const next = adjacentAdminUserFormSection(sections, activeId, 1);
    if (next) goTo(next.id);
  }, [sections, activeId, goTo]);

  const goPrev = useCallback(() => {
    const prev = adjacentAdminUserFormSection(sections, activeId, -1);
    if (prev) goTo(prev.id);
  }, [sections, activeId, goTo]);

  const activeIndex = Math.max(
    0,
    sections.findIndex((s) => s.id === activeId)
  );
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex >= 0 && activeIndex < sections.length - 1;

  return {
    sections,
    activeId,
    goTo,
    goNext,
    goPrev,
    hasPrev,
    hasNext,
  };
}
